/**
 * scheduleDates — campaign start/end date transforms for the admin console.
 *
 * EVERY FUNCTION HERE IS A STRING TRANSFORM. No stored value is ever fed through
 * `new Date()` and back out again, and that is the entire defence against the
 * off-by-one this repo has already been bitten by twice (see the header of
 * fmtDate.js). Two concrete traps it avoids:
 *
 *   new Date('2026-10-31 00:00:00').toISOString().slice(0,10)
 *     V8 parses the space form as LOCAL time, then toISOString shifts to UTC, so
 *     any viewer east of UTC gets '2026-10-30'.
 *
 *   new Date('2026-10-31').toLocaleDateString()
 *     The bare date form is parsed as UTC MIDNIGHT, so any viewer west of UTC
 *     gets Oct 30. This was live in the launch-date panel this replaces, which
 *     rendered a stored launch date via new Date(v).toLocaleDateString("en-US").
 *
 * Being string-only also makes these functions timezone-independent by
 * construction rather than by test setup, which is why their spec needs no
 * process.env.TZ juggling.
 *
 * TWO STORED FORMATS. The API column is VARCHAR and holds both
 * 'YYYY-MM-DD HH:mm:ss' (the cron and, as of 2026-07-31, every writer) and
 * '…T…Z' (legacy rows). Everything here accepts both.
 */

/** Leading calendar date of any stored shape, or ''. */
export function toDateInputValue(stored) {
  if (!stored) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(stored))
  return m ? `${m[1]}-${m[2]}-${m[3]}` : ''
}

/** 'YYYY-MM-DDTHH:mm' for <input type="datetime-local">, or ''. */
export function toDateTimeLocalValue(stored) {
  if (!stored) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(stored))
  if (m) return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}`
  const d = toDateInputValue(stored)
  return d ? `${d}T00:00` : ''
}

/**
 * What the API is sent. The server stores this verbatim as a wall clock in the
 * platform's business timezone (America/Toronto) — see campaignSchedule.js — so
 * the console must NOT convert. A datetime-local value the browser reinterprets
 * is exactly how a timezone gets invented.
 */
export function toApiDateTime(inputValue) {
  if (!inputValue) return null
  const s = String(inputValue).trim()
  const dt = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(s)
  if (dt) return `${dt[1]} ${dt[2]}:${dt[3]}:${dt[4] || '00'}`
  const d = /^(\d{4}-\d{2}-\d{2})$/.exec(s)
  if (d) return `${d[1]} 00:00:00`
  return null
}

/**
 * launch + 3 months, matching the API's defaultCampaignEndDate. Month arithmetic
 * on the calendar date only — no Date object, so no drift. Clamps a rolled-over
 * day (31 Jan + 1 month) to the last day of the target month, which is what
 * moment's add(1,'M') does on the server.
 */
export function defaultEndFromLaunch(launchDate, months = 3) {
  const base = toDateInputValue(launchDate)
  if (!base) return ''
  const [y, m, d] = base.split('-').map(Number)
  const total = (m - 1) + months
  const year = y + Math.floor(total / 12)
  const month = (total % 12) + 1
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const day = Math.min(d, lastDay)
  const pad = n => String(n).padStart(2, '0')
  const time = /[ T](\d{2}:\d{2}:\d{2})/.exec(String(launchDate))
  return `${year}-${pad(month)}-${pad(day)} ${time ? time[1] : '00:00:00'}`
}

/** True when `next` closes the campaign EARLIER than `current`. */
export function isShortening(currentEnd, nextEnd) {
  const a = toDateInputValue(currentEnd)
  const b = toDateInputValue(nextEnd)
  if (!a || !b) return false
  return b < a // both 'YYYY-MM-DD', so lexicographic IS chronological
}

/** Whole days between two stored dates; negative when `next` is earlier. */
export function daysBetween(currentEnd, nextEnd) {
  const a = toDateInputValue(currentEnd)
  const b = toDateInputValue(nextEnd)
  if (!a || !b) return 0
  const [ay, am, ad] = a.split('-').map(Number)
  const [by, bm, bd] = b.split('-').map(Number)
  const ms = Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)
  return Math.round(ms / 86400000)
}

/** Today, as a value for an <input type="date"> `min` attribute. */
export function todayDateInputValue(now = new Date()) {
  const pad = n => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/**
 * The platform's business timezone. The API stores and compares campaign dates
 * as this zone's wall clock (campaignSchedule.js), so "now" for a go-live check
 * is this clock, not the viewer's.
 */
export const BUSINESS_TIME_ZONE = 'America/Toronto'

let _businessClockFmt = null
function businessClockFmt() {
  if (!_businessClockFmt) {
    _businessClockFmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: BUSINESS_TIME_ZONE,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hourCycle: 'h23', // midnight is 00, never 24
    })
  }
  return _businessClockFmt
}

/** The business-timezone wall clock at an instant, as its formatted parts. */
function businessWallParts(ms) {
  const part = {}
  businessClockFmt().formatToParts(new Date(ms)).forEach(p => { part[p.type] = p.value })
  return part
}

/** That wall clock read as if it were UTC: what a zone offset is measured against. */
function businessWallAsUtc(ms) {
  const p = businessWallParts(ms)
  return Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day),
    Number(p.hour), Number(p.minute), Number(p.second))
}

/**
 * The business-timezone wall clock at `now` plus `addMinutes`, as a
 * datetime-local value 'YYYY-MM-DDTHH:mm', rounded UP to the whole minute (the
 * part of a minute already gone cannot be chosen). This reads the CURRENT
 * instant, never a stored value, so the module's no-Date-round-trip rule is
 * untouched. It is for display and an input's `min` only: DECISIONS compare
 * instants (goLiveError), because on the fall-back day the same wall-clock
 * string names two different moments.
 */
export function businessDateTimeLocalValue(now = new Date(), addMinutes = 0) {
  const minute = 60 * 1000
  const part = businessWallParts(Math.ceil((now.getTime() + addMinutes * minute) / minute) * minute)
  return `${part.year}-${part.month}-${part.day}T${part.hour}:${part.minute}`
}

const WALL_CLOCK_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/

/**
 * The INSTANT a business-timezone wall time names, as { ms }; or { error } when
 * it is 'invalid' (unreadable, or not a real date and time), 'nonexistent'
 * (skipped by the spring-forward change) or 'ambiguous' (the fall-back hour,
 * which happens twice, so the API's wall-clock string cannot say which).
 *
 * Accepts the datetime-local shape and the API's 'YYYY-MM-DD HH:mm:ss'. Intl
 * only: the offsets in force a day either side are each tried, and a candidate
 * is kept only if the zone's clock really reads that wall time at it.
 */
export function businessWallClockToInstant(value) {
  const m = WALL_CLOCK_RE.exec(String(value ?? '').trim())
  if (!m) return { error: 'invalid' }
  const [y, mo, d, h, mi, sec] = [m[1], m[2], m[3], m[4], m[5], m[6] || '0'].map(Number)
  const wallAsUtc = Date.UTC(y, mo - 1, d, h, mi, sec)
  const echo = new Date(wallAsUtc)
  if (echo.getUTCFullYear() !== y || echo.getUTCMonth() !== mo - 1 || echo.getUTCDate() !== d
    || echo.getUTCHours() !== h || echo.getUTCMinutes() !== mi || echo.getUTCSeconds() !== sec) {
    return { error: 'invalid' } // 30 Feb, 24:00, 09:60 ...
  }
  const day = 24 * 60 * 60 * 1000
  const offsets = new Set([-day, day].map(shift => businessWallAsUtc(wallAsUtc + shift) - (wallAsUtc + shift)))
  const instants = Array.from(offsets)
    .map(offset => wallAsUtc - offset)
    .filter(ms => businessWallAsUtc(ms) === wallAsUtc)
  if (instants.length === 0) return { error: 'nonexistent' }
  if (instants.length > 1) return { error: 'ambiguous' }
  return { ms: instants[0] }
}

/**
 * A scheduled go-live must be at least this far ahead when it is checked, so
 * the time the confirm dialog takes cannot carry it into the past (where the
 * API would publish at once instead of scheduling).
 */
export const GO_LIVE_MIN_LEAD_MINUTES = 5

export const GO_LIVE_ERROR_COPY = {
  invalid: 'Enter a valid date and time, or select Now.',
  dst: 'That time falls in the daylight-saving change. Choose a different time.',
  passed: 'That time has passed. Choose a later time, or select Now.',
}

/**
 * Why a scheduled go-live (a business-timezone wall time, datetime-local or API
 * format) cannot be used at `now`, or null when it can. Compares INSTANTS, never
 * wall-clock strings, and refuses the DST-change times outright: the API stores
 * a Toronto wall-clock string, which cannot tell the two 01:xx hours apart.
 */
export function goLiveError(value, now = new Date(), leadMinutes = GO_LIVE_MIN_LEAD_MINUTES) {
  const { ms, error } = businessWallClockToInstant(value)
  if (error === 'invalid') return GO_LIVE_ERROR_COPY.invalid
  if (error) return GO_LIVE_ERROR_COPY.dst
  if (ms < now.getTime() + leadMinutes * 60 * 1000) return GO_LIVE_ERROR_COPY.passed
  return null
}

/**
 * Server refusal codes → operator-facing copy. Keyed on `code`, never on the
 * server's message text: the message is prose that will be reworded, the code is
 * the contract.
 */
export const SCHEDULE_ERROR_COPY = {
  campaign_id_required: 'That campaign could not be identified. Reload the page and try again.',
  campaign_not_found: 'This campaign is no longer in the system.',
  no_schedule_fields: 'Set a start date, an end date, or both before saving.',
  invalid_launch_date: 'That start date is not a valid date before 19 January 2038.',
  invalid_end_date: 'That end date could not be read. Use the date picker.',
  end_date_not_after_start: 'The end date must fall after the start date.',
  end_date_in_past:
    'That end date has already passed, so the campaign would close on the next sweep. '
    + 'Use Close Campaign if that is what you intend.',
  shorten_requires_confirmation:
    'Shortening a live campaign needs confirmation. Try again and confirm the warning.',
  launch_date_immutable_after_publish:
    'This campaign is already live, so its start date is a matter of record. '
    + 'Use Withdraw and Reschedule to take it off the market first.',
  schedule_not_allowed_for_status:
    'This campaign is not in a state where its schedule can be changed.',
  fund_schedule_unsupported:
    'Fund campaigns run continuously and are not closed by an end date.',
}
